import { GetStaticPropsResult } from 'next'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import { useEffect } from 'react'

import { accountPageModuleQuery } from '@data/sanity/queries/page'
import { SanityAccountPage } from '@data/sanity/queries/types/page'
import { SanityProductCatalogueQuery } from '@data/sanity/queries/types/product'
import { SanitySiteFragment } from '@data/sanity/queries/types/site'
import { getPageSanityClient } from '@lib/sanity/client'
import { defaultModuleQueryParameters, getAccountPage } from '@lib/sanity/page'
import { getProductCatalogue } from '@lib/sanity/product'
import { useUser } from '@lib/auth'
import { Locale } from '@lib/language'
import { CustomStaticPropsContext, getPageUrl, PageType } from '@lib/routes'

import Layout from '@components/layout'
import Modules from '@modules/modules'
import PreviewModules from '@modules/preview-modules'

const PreviewProvider = dynamic(() => import('@lib/sanity/preview'))

interface AccountPageProps {
  draftMode: boolean
  draftToken?: string
  locale: Locale
  page: SanityAccountPage
  site: SanitySiteFragment
  productCatalogue: SanityProductCatalogueQuery
}

const AccountPage = ({
  draftMode,
  draftToken,
  locale,
  page,
  site,
}: AccountPageProps) => {
  const router = useRouter()
  const { user } = useUser()

  // Redirect to login page if not logged in
  useEffect(() => {
    // Do not redirect in draft mode
    if (draftMode && draftToken) {
      return
    }

    if (user && !user.isLoggedIn) {
      router.push(getPageUrl(PageType.LOGIN_PAGE))
    }
  }, [draftMode, draftToken, router, user])

  if (draftMode && draftToken) {
    return (
      <Layout page={page} site={site}>
        <PreviewProvider draftToken={draftToken}>
          <PreviewModules
            modules={page.modules}
            moduleQuery={accountPageModuleQuery}
            moduleQueryParams={{
              ...defaultModuleQueryParameters,
              locale,
            }}
          />
        </PreviewProvider>
      </Layout>
    )
  }

  if (!user?.isLoggedIn) {
    return null
  }

  return (
    <Layout page={page} site={site}>
      <Modules modules={page.modules} />
    </Layout>
  )
}

export async function getStaticProps(
  context: CustomStaticPropsContext
): Promise<GetStaticPropsResult<AccountPageProps>> {
  const [sanityClient, draftMode, draftToken] = getPageSanityClient(
    context.draftMode
  )
  const accountPage = await getAccountPage(sanityClient, context.locale)

  if (!accountPage.page) {
    return {
      notFound: true,
    }
  }

  // Get all products
  const productCatalogue = await getProductCatalogue(
    sanityClient,
    context.locale
  )

  return {
    props: {
      draftMode,
      draftToken,
      locale: context.locale,
      page: accountPage.page,
      site: accountPage.site,
      productCatalogue,
    },
    revalidate: 60,
  }
}

export default AccountPage
